import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Check } from "lucide-react";
import { bookCall } from "../lib/api";

const initial = { name: "", email: "", company: "", role: "", topic: "", preferred_time: "", message: "" };

export const BookingModal = ({ open, onClose, topic }) => {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState("idle");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (open) { setForm({ ...initial, topic: topic || "" }); setStatus("idle"); }
  }, [open, topic]);

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await bookCall(form);
      setStatus("done");
      setForm(initial);
    } catch {
      setStatus("error");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="booking-modal"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
            transition={{ ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto p-8 sm:p-10 relative"
          >
            <button data-testid="booking-close" onClick={onClose} className="absolute top-5 right-5 text-[var(--ink-soft)] hover:text-[var(--petrol)]"><X strokeWidth={1.5} /></button>

            {status === "done" ? (
              <div className="py-10 text-center" data-testid="booking-success">
                <div className="w-14 h-14 mx-auto mb-6 rounded-full bg-[var(--petrol)] flex items-center justify-center text-white"><Check strokeWidth={2} /></div>
                <h3 className="font-serif text-3xl text-[var(--ink)] mb-2">Richiesta inviata</h3>
                <p className="text-[var(--ink-soft)]">Ti risponderemo al più presto per fissare la tua Executive Call.</p>
                <button onClick={onClose} className="btn-fill mt-8 text-xs uppercase tracking-[0.15em] font-semibold border border-[var(--petrol)] text-[var(--petrol)] hover:text-white px-8 py-3">Chiudi</button>
              </div>
            ) : (
              <>
                <div className="overline mb-3">Executive Call</div>
                <h3 className="font-serif text-3xl text-[var(--ink)] mb-2">Prenota una Executive Call</h3>
                <p className="text-xs uppercase tracking-[0.15em] text-[var(--accent)] mb-3">30 minuti · gratuita · senza impegno</p>
                <p className="text-sm text-[var(--ink-soft)] leading-relaxed mb-6">Un primo confronto per comprendere priorità, sfide e possibili aree di intervento.</p>
                <form onSubmit={submit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Input testid="book-name" placeholder="Nome*" value={form.name} onChange={set("name")} required />
                    <Input testid="book-email" type="email" placeholder="Email*" value={form.email} onChange={set("email")} required />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Input testid="book-company" placeholder="Azienda" value={form.company} onChange={set("company")} />
                    <Input testid="book-role" placeholder="Ruolo" value={form.role} onChange={set("role")} />
                  </div>
                  <Input testid="book-topic" placeholder="Tema della call*" value={form.topic} onChange={set("topic")} required />
                  <Input testid="book-time" placeholder="Orario preferito (es. mattina, martedì)" value={form.preferred_time} onChange={set("preferred_time")} />
                  <textarea data-testid="book-message" placeholder="Note aggiuntive" value={form.message} onChange={set("message")} rows={3}
                    className="w-full border border-[var(--line)] px-4 py-3 text-sm focus:border-[var(--petrol)] outline-none resize-none" />
                  {status === "error" && <p className="text-sm text-red-600" data-testid="booking-error">Invio non riuscito. Riprova.</p>}
                  <button data-testid="booking-submit" disabled={status === "loading"}
                    className="btn-fill w-full inline-flex justify-center items-center gap-2 text-xs uppercase tracking-[0.15em] font-semibold bg-[var(--petrol)] text-white px-8 py-4 disabled:opacity-60">
                    {status === "loading" ? <Loader2 size={16} className="animate-spin" /> : "Invia richiesta"}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Input = ({ testid, ...props }) => (
  <input data-testid={testid} {...props}
    className="w-full border border-[var(--line)] px-4 py-3 text-sm focus:border-[var(--petrol)] outline-none" />
);
